import Service from '@ember/service';
import { service } from '@ember/service';
import { computed, set } from '@ember/object';
import TabData from '../data/tabdata';
import Utils from '../utils/utils';
import Constants from '../utils/constants';
import cloneDeep from 'lodash.clonedeep';

export default class TabService extends Service {
    @service errorHandling;

    tabs = { items: {}, lastUpdateDate: null };

    /**
     * Generate lists of tabs and set initial selected tab for each list
     */
    load(urlValues) {
        Object.keys(TabData.tabs).forEach((tabType) => {
            let tabTypeMetadata = TabData.tabs[tabType],
                urlKey = tabTypeMetadata.urlKey,
                tabList = this.generateTabList(tabTypeMetadata.items, tabType),
                foundTab = null;

            if (tabList && tabList.length && urlKey && urlKey in urlValues && Utils.isNumeric(urlValues[urlKey])) {
                foundTab = tabList.find((tab) => tab.index == urlValues[urlKey]);
                if (!foundTab) foundTab = tabList.find((tab) => tab.default);
                if (!foundTab) foundTab = tabList[0];
            } else if (tabList && tabList.length) {
                foundTab = tabList.find((tab) => tab.default);
                if (!foundTab) foundTab = tabList[0];
            }

            if (foundTab) set(foundTab, 'isSelected', true);
            this.set(`tabs.items.${tabType}`, tabList || []);
        });
    }

    @computed('tabs.lastUpdateDate') get selected() {
        let result = {};
        Object.keys(this.tabs.items).forEach((tabType) => {
            let selectedTab = this.tabs.items[tabType].find((item) => item.isSelected);
            result[tabType] = selectedTab;
        });

        return result;
    }

    goToTab(tab) {
        let tabList = tab.type.name in this.tabs.items ? this.tabs.items[tab.type.name] : [],
            foundTab = tabList.find((other) => other.name === tab.name),
            selectedTab = tabList.find((other) => other.isSelected);

        if (foundTab && ((selectedTab && foundTab.name != selectedTab.name) || !selectedTab)) {
            set(foundTab, 'isSelected', true);
            if (selectedTab) set(selectedTab, 'isSelected', false);
        }
    }

    generateTab(type, key) {
        let tabType = TabData.tabs[type],
            tab = cloneDeep(tabType.items[key]),
            result = {};

        result.metadata = tab;

        result.name = tab.name;
        result.description = tab.description;
        result.index = tab.index;
        result.qaAttrValue = tab.qaAttrValue;

        result.type = {
            name: type,
            qaAttrValue: tabType.qaAttrValue,
            isMain: type === Constants.TAB_TYPE.MAIN,
            urlKey: tabType.urlKey,
            sendAnalyticsEventOnTabChange: tabType.sendAnalyticsEventOnTabChange || false,
        };

        result.default = tab.default || false;
        result.hasIcon = tab.hasIcon || false;

        Utils.generateNamingMetadata(result);
        return result;
    }

    generateTabList(dataTabs, tabType) {
        return Object.keys(dataTabs)
            .map((key) => this.generateTab(tabType, key))
            .sort(this.sortTabs);
    }

    sortTabs(tab1, tab2) {
        return Utils.compare(tab1.index, tab2.index);
    }

    updateTabs(model) {
        let tabModel = {
            notInDebugMode: !model.debugMode,
            selectedAdcHasFixedKickbackType: model.selectedAdc.kickbackType === 'fixed',
            isNotMultiplexed: !model.multiplexed,
            isInternalMultiplexed: model.selectedAdc.internalMux,
        };

        Object.keys(this.tabs.items).forEach((tabType) => {
            this.tabs.items[tabType].forEach((tab) => {
                let error = null,
                    errorItems = [],
                    errorsExist = false,
                    hide = false;

                if (tab.metadata.overrideTabError && tab.metadata.error in this.errorHandling)
                    error = this.errorHandling[tab.metadata.error];
                else if (!tab.metadata.overrideTabError && this.errorHandling.tabError)
                    error = this.errorHandling.tabError;
                else if (tab.metadata.error && tab.metadata.error in this.errorHandling) {
                    error = this.errorHandling[tab.metadata.error];
                    if (tab.metadata.errorItems && tab.metadata.errorItems in this.errorHandling)
                        errorItems = this.errorHandling[tab.metadata.errorItems];
                }

                errorsExist =
                    tab.metadata.errorsExist && this.errorHandling.get(tab.metadata.errorsExist)
                        ? this.errorHandling.get(tab.metadata.errorsExist)
                        : false;

                let tabsMetHideRequirements = (tab.metadata.hideRequirements || []).filter(
                    (requirement) => requirement in tabModel && tabModel[requirement],
                );

                // A default tab should never be hidden
                hide = tab.default ? false : tabsMetHideRequirements.length ? true : false;

                set(tab, 'error', error);
                set(tab, 'errorItems', errorItems);
                set(tab, 'errorsExist', errorsExist);
                set(tab, 'hide', hide);
            });

            let hiddenSelectedTab = this.tabs.items[tabType].find((tab) => tab.isSelected && tab.hide);
            if (hiddenSelectedTab) {
                let defaultTab = this.tabs.items[tabType].find((tab) => tab.default);
                this.goToTab(defaultTab);
            }
        });

        this.set('tabs.lastUpdateDate', new Date());
    }
}
